import { Node } from "@iventis/domain-model/model/node";
import { NodeType } from "@iventis/domain-model/model/nodeType";
import { Body2, inlineTextIconMargin, tileViewGap, styled } from "@iventis/styles";
import { Content } from "@iventis/translations";
import { NodeWithHierarchy } from "@iventis/tree-browser";
import { useIventisTranslate } from "@iventis/translations/use-iventis-translate";
import React, { useMemo } from "react";
import { useMaxElementCount } from "@iventis/utilities";
import { useDispatch } from "react-redux";
import { useHistory } from "@iventis/subscriptions";
import { BasicSkeletonTileNode, BasicTileNode } from "./basic-tile-node";
import globe from "../assets/globe.jpg";
import { StaticLibrary, createHrefToNode } from "./mapping-library.helpers";
import { LibraryTileNodePreview } from "./library-tile-node-preview";
import { EmptyDashboardWidgetComponent } from "./empty-widget";
import { MapThumbnailPreview } from "./library-nodes";
import { getLibraryFromBottomUpHierarchy } from "./bottom-up-breadcrumb";
import { RootDispatch } from "../state/root.store";
import { sidebar } from "../state/sidebar.slice";
import { navigateToMap } from "../map.helpers";

interface BasicTileNodeContainerProps {
    tree: NodeWithHierarchy;
    thumbnails: { [nodeId: string]: string };
    content: (node: Node) => React.ReactNode;
    staticLibrary: StaticLibrary;
    updateNode: (updatedNode: Node) => void;
    onClickEmpty: () => void;
    includeNodeBreadcrumb?: boolean;
    isLoading: boolean;
}

export const BasicTileNodeContainer: React.FunctionComponent<BasicTileNodeContainerProps> = ({
    tree,
    thumbnails,
    content,
    staticLibrary,
    updateNode,
    onClickEmpty,
    includeNodeBreadcrumb = false,
    isLoading,
}) => {
    const history = useHistory();
    const dispatch: RootDispatch = useDispatch();
    const translate = useIventisTranslate();

    const open = (event, node: NodeWithHierarchy) => {
        // Goto the map
        if (node.type === NodeType.Folder) {
            const treeNode = getLibraryFromBottomUpHierarchy(node.bottomUpHierarchy);
            history.push(createHrefToNode(treeNode.id, node.id));
        } else if (node.type === NodeType.Map) {
            const treeNode = getLibraryFromBottomUpHierarchy(node.bottomUpHierarchy);
            dispatch(sidebar.actions.setPreviousLibraryId(treeNode.id));
            navigateToMap(node.id, node.parentId, history);
        }
        // Stops the event going further in the DOM which made the node to be toggled twice
        event.preventDefault();
    };

    const [maxElementContainerRef, maxNodeCount] = useMaxElementCount<HTMLDivElement>(215, 260, { rows: 1, roundUp: false });

    const skeletons = useMemo(
        () =>
            new Array(maxNodeCount)
                .fill(true)
                // eslint-disable-next-line react/no-array-index-key
                .map((_, i) => <BasicSkeletonTileNode sequenceNumber={i} totalElementCount={maxNodeCount} key={i} includeBreadcrumb={includeNodeBreadcrumb} />),
        [maxNodeCount]
    );

    return (
        <StyledContainer
            ref={(el) => {
                maxElementContainerRef.current = el;
            }}
        >
            {!isLoading && tree ? (
                tree.childNodes.length > 0 ? (
                    tree.childNodes
                        .slice(0, maxNodeCount)
                        .map((node: NodeWithHierarchy) => (
                            <BasicTileNode
                                key={node.id}
                                node={node}
                                preview={
                                    node.type === NodeType.Folder ? (
                                        <LibraryTileNodePreview childCount={node.childCount} />
                                    ) : thumbnails && thumbnails[node.id] ? (
                                        <MapThumbnailPreview backgroundImageUrl={thumbnails[node.id]} />
                                    ) : (
                                        <MapThumbnailPreview backgroundImageUrl={globe} />
                                    )
                                }
                                content={content(node)}
                                updateNode={updateNode}
                                open={open}
                            />
                        ))
                ) : (
                    <EmptyDashboardWidgetComponent
                        icon={["far", staticLibrary.icon]}
                        buttonText={translate(Content.library.static_library_empty_button[staticLibrary.translationKey])}
                        onClick={onClickEmpty}
                    >
                        <Body2 style={{ flex: "1 0", marginRight: inlineTextIconMargin }}>{translate(Content.library.static_library_empty[staticLibrary.translationKey])}</Body2>
                    </EmptyDashboardWidgetComponent>
                )
            ) : (
                skeletons
            )}
        </StyledContainer>
    );
};

const StyledContainer = styled.div`
    display: flex;
    flex-direction: row;
    flex-wrap: nowrap;
    overflow: hidden;
    gap: ${tileViewGap};

    height: 100%;

    .card {
        height: fit-content;
    }
`;
